<template>
  <div class="role-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('role.management') }}</h2>
        <div class="header-actions">
          <a-space>
            <a-button type="primary" @click="showCreateModal">
              <template #icon><UserAddOutlined /></template>
              {{ $t('role.createRole') }}
            </a-button>
            <a-button @click="showPermissionMatrix">
              <template #icon><TableOutlined /></template>
              {{ $t('role.permissionMatrix') }}
            </a-button>
            <a-button @click="showTemplateModal">
              <template #icon><FileTextOutlined /></template>
              {{ $t('role.roleTemplate') }}
            </a-button>
          </a-space>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <a-row :gutter="16">
        <a-col :span="6">
          <a-card>
            <a-statistic
              :title="$t('role.totalRoles')"
              :value="stats.total_roles"
              :loading="statsLoading"
            />
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card>
            <a-statistic
              :title="$t('role.systemRoles')"
              :value="stats.system_roles"
              :loading="statsLoading"
              :value-style="{ color: '#1890ff' }"
            />
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card>
            <a-statistic
              :title="$t('role.customRoles')"
              :value="stats.custom_roles"
              :loading="statsLoading"
              :value-style="{ color: '#52c41a' }"
            />
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card>
            <a-statistic
              :title="$t('role.totalPermissions')"
              :value="stats.total_permissions"
              :loading="statsLoading"
            />
          </a-card>
        </a-col>
      </a-row>
    </div>

    <!-- 搜索和筛选 -->
    <a-card class="search-card">
      <a-form layout="inline" :model="searchForm" @finish="handleSearch">
        <a-form-item :label="$t('common.search')">
          <a-input
            v-model:value="searchForm.search"
            :placeholder="$t('role.searchPlaceholder')"
            style="width: 200px"
            allow-clear
          />
        </a-form-item>
        <a-form-item :label="$t('common.status')">
          <a-select
            v-model:value="searchForm.status"
            :placeholder="$t('user.allStatuses')"
            style="width: 120px"
            allow-clear
          >
            <a-select-option value="active">{{ $t('user.active') }}</a-select-option>
            <a-select-option value="inactive">{{ $t('user.inactive') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('role.type')">
          <a-select
            v-model:value="searchForm.is_system"
            :placeholder="$t('user.allTypes')"
            style="width: 120px"
            allow-clear
          >
            <a-select-option :value="true">{{ $t('role.systemRole') }}</a-select-option>
            <a-select-option :value="false">{{ $t('role.customRole') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">
              {{ $t('common.search') }}
            </a-button>
            <a-button @click="resetSearch">{{ $t('user.reset') }}</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 角色列表 -->
    <a-card>
      <template #title>
        <div class="table-header">
          <span>{{ $t('role.roleList') }}</span>
          <div class="table-actions">
            <a-space>
              <span v-if="selectedRowKeys.length > 0">
                {{ $t('user.selectedItems', { count: selectedRowKeys.length }) }}
              </span>
              <a-button
                v-if="selectedRowKeys.length > 0"
                type="primary"
                danger
                @click="batchDelete"
              >
                {{ $t('user.batchDelete') }}
              </a-button>
            </a-space>
          </div>
        </div>
      </template>

      <a-table
        :columns="columns"
        :data-source="roles"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1200 }"
        :row-key="(record: Role) => record.id"
        @change="handleTableChange"
      >
        <template #headerCell="{ column }">
          <template v-if="column.key === 'selection'">
            <a-checkbox
              :checked="checkAll"
              :indeterminate="false"
              @change="(e: any) => onCheckAllChange(e.target.checked)"
            />
          </template>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'selection'">
            <a-checkbox
              :checked="selectedRowKeys.includes(record.id)"
              :disabled="record.is_system"
              :indeterminate="false"
              @change="(e: any) => onCheckboxChange(record, e.target.checked)"
            />
          </template>

          <template v-if="column.key === 'name'">
            <div class="role-name">
              <span class="name">{{ record.name }}</span>
              <a-tag v-if="record.is_system" color="blue" size="small">{{ $t('role.system') }}</a-tag>
              <a-tag v-if="record.is_default" color="green" size="small">{{ $t('role.default') }}</a-tag>
            </div>
          </template>

          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ getStatusText(record.status) }}
            </a-tag>
          </template>

          <template v-if="column.key === 'permissions'">
            <a-space>
              <!-- 过滤掉空权限，确保计算准确 -->
              <template v-for="perm in (record.permissions || []).filter((p: any) => p && p.id && p.name).slice(0, 3)" :key="perm.id">
                <a-tag size="small">
                  {{ perm.name }}
                </a-tag>
              </template>
              <a-tag v-if="(record.permissions || []).filter((p: any) => p && p.id && p.name).length > 3" size="small">
                +{{ (record.permissions || []).filter((p: any) => p && p.id && p.name).length - 3 }}
              </a-tag>
            </a-space>
          </template>

          <template v-if="column.key === 'user_count'">
            <a-button type="link" @click="viewRoleUsers(record)">
              {{ record.user_count || 0 }} {{ $t('role.users') }}
            </a-button>
          </template>

          <template v-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="viewRole(record)">
                {{ $t('common.view') }}
              </a-button>
              <a-button
                type="link"
                size="small"
                @click="editRole(record)"
                :title="record.is_system ? $t('role.editPermissionsOnly') : undefined"
              >
                {{ $t('common.edit') }}
              </a-button>
              <a-dropdown>
                <a-button type="link" size="small">
                  {{ $t('common.more') }} <DownOutlined />
                </a-button>
                <template #overlay>
                  <a-menu>
                    <a-menu-item @click="assignUsers(record)">
                      {{ $t('role.assignUsers') }}
                    </a-menu-item>
                    <a-menu-divider />
                    <a-menu-item
                      @click="deleteRole(record)"
                      :disabled="record.is_system"
                      class="text-red"
                    >
                      {{ $t('user.delete') }}
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 创建/编辑角色弹窗 -->
    <a-modal
      v-model:open="roleModalVisible"
      :title="isEditing ? $t('role.editRole') : $t('role.createRole')"
      :width="800"
      @ok="handleRoleSubmit"
      @cancel="handleRoleCancel"
      :confirm-loading="submitting"
    >
      <a-form
        ref="roleFormRef"
        :model="roleForm"
        :rules="roleFormRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
      >
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('role.roleName')" name="name">
              <a-input
                v-model:value="roleForm.name"
                :placeholder="$t('role.enterRoleName')"
                :disabled="isEditing && selectedRole?.is_system"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('role.roleCode')" name="code">
              <a-input
                v-model:value="roleForm.code"
                :placeholder="$t('role.enterRoleCode')"
                :disabled="isEditing && selectedRole?.is_system"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('role.parentRole')">
              <a-tree-select
                v-model:value="roleForm.parent_id"
                :tree-data="roleTreeData"
                :placeholder="$t('role.selectParentRole')"
                allow-clear
                tree-default-expand-all
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('role.sortOrder')">
              <a-input-number
                v-model:value="roleForm.sort_order"
                :min="0"
                :placeholder="$t('role.sortValue')"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-form-item :label="$t('role.roleDescription')">
          <a-textarea
            v-model:value="roleForm.description"
            :placeholder="$t('role.enterRoleDescription')"
            :rows="3"
          />
        </a-form-item>

        <a-form-item :label="$t('role.permissionConfig')">
          <a-alert
            v-if="isEditing && selectedRole?.is_system"
            type="info"
            :message="$t('role.editPermissionsOnly')"
            show-icon
            class="permission-alert"
          />
          <div class="permission-config">
            <div class="permission-header">
              <a-space>
                <a-button size="small" @click="selectAllPermissions">{{ $t('role.selectAll') }}</a-button>
                <a-button size="small" @click="clearAllPermissions">{{ $t('role.clearAll') }}</a-button>
                <a-select
                  v-model:value="selectedModule"
                  :placeholder="$t('role.filterByModule')"
                  style="width: 150px"
                  size="small"
                  allow-clear
                  @change="filterPermissionsByModule"
                >
                  <a-select-option v-for="(label, key) in PERMISSION_MODULES" :key="key" :value="key">
                    {{ label }}
                  </a-select-option>
                </a-select>
              </a-space>
            </div>
            
            <div class="permission-tree">
              <a-tree
                v-model:checkedKeys="roleForm.permission_ids"
                :tree-data="permissionTreeData"
                checkable
                :height="300"
                :default-expand-all="true"
              />
            </div>
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 角色详情弹窗 -->
    <a-modal
      v-model:open="roleDetailVisible"
      :title="$t('role.roleDetails')"
      :width="800"
      :footer="null"
    >
      <div v-if="selectedRole" class="role-detail">
        <a-descriptions :column="2" bordered>
          <a-descriptions-item :label="$t('role.roleName')">
            {{ selectedRole.name }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('role.roleCode')">
            {{ selectedRole.code }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('role.roleType')">
            <a-space>
              <a-tag v-if="selectedRole.is_system" color="blue">{{ $t('role.systemRole') }}</a-tag>
              <a-tag v-else color="green">{{ $t('role.customRole') }}</a-tag>
              <a-tag v-if="selectedRole.is_default" color="orange">{{ $t('role.defaultRole') }}</a-tag>
            </a-space>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('user.status')">
            <a-tag :color="getStatusColor(selectedRole.status)">
              {{ getStatusText(selectedRole.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('role.parentRole')">
            {{ selectedRole.parent?.name || $t('common.none') }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('role.userCount')">
            {{ selectedRole.user_count || 0 }} {{ $t('role.users') }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('user.createTimeLabel')">
            {{ formatDateTime(selectedRole.created_at) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('user.updateTimeLabel')">
            {{ formatDateTime(selectedRole.updated_at) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('role.roleDescription')" :span="2">
            {{ selectedRole.description || $t('common.none') }}
          </a-descriptions-item>
        </a-descriptions>

        <a-divider>{{ $t('role.permissionList') }}</a-divider>
        <div class="permission-list">
          <a-row :gutter="[16, 16]">
            <a-col v-for="perm in (selectedRole?.permissions || [])" :key="perm.id" :span="8">
              <a-card size="small">
                <div class="permission-item">
                  <div class="permission-name">{{ perm.name }}</div>
                  <div class="permission-code">{{ perm.code }}</div>
                  <div class="permission-module">
                    <a-tag size="small">{{ PERMISSION_MODULES[perm.module as keyof typeof PERMISSION_MODULES] || perm.module }}</a-tag>
                  </div>
                </div>
              </a-card>
            </a-col>
          </a-row>
        </div>
      </div>
    </a-modal>

    <!-- 权限矩阵弹窗 -->
    <a-modal
      v-model:open="matrixModalVisible"
      :title="$t('role.permissionMatrix')"
      :width="1200"
      :footer="null"
    >
      <div class="permission-matrix">
        <a-table
          :columns="matrixColumns"
          :data-source="matrixData"
          :pagination="false"
          :scroll="{ x: 1000, y: 400 }"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key !== 'role'">
              <a-checkbox
                :checked="record[column.key]"
                @change="(e: any) => handleMatrixChange(record.roleId, column.permissionId, e.target.checked)"
              />
            </template>
          </template>
        </a-table>
      </div>
    </a-modal>

    <!-- 角色模板弹窗 -->
    <a-modal
      v-model:open="templateModalVisible"
      :title="$t('role.roleTemplate')"
      :width="800"
      :footer="null"
    >
      <div class="role-templates">
        <a-list :data-source="roleTemplates" :loading="templatesLoading">
          <template #renderItem="{ item }">
            <a-list-item>
              <template #actions>
                <a-button type="link" @click="applyTemplate(item)">{{ $t('role.applyTemplate') }}</a-button>
                <a-button type="link" @click="viewTemplate(item)">{{ $t('role.viewTemplate') }}</a-button>
              </template>
              <a-list-item-meta>
                <template #title>
                  <div class="template-title">
                    {{ item.name }}
                    <a-tag v-if="item.is_builtin" color="blue" size="small">{{ $t('role.builtin') }}</a-tag>
                  </div>
                </template>
                <template #description>
                  {{ item.description }}
                </template>
              </a-list-item-meta>
            </a-list-item>
          </template>
        </a-list>
      </div>
    </a-modal>

    <!-- 用户分配弹窗 -->
    <a-modal
      v-model:open="assignModalVisible"
      :title="$t('role.assignUsers')"
      :width="600"
      @ok="handleUserAssign"
      @cancel="assignModalVisible = false"
      :confirm-loading="assigning"
    >
      <div class="user-assign">
        <a-transfer
          v-model:target-keys="assignForm.user_ids"
          :data-source="allUsers"
          :titles="[$t('role.availableUsers'), $t('role.selectedUsers')]"
          :render="(item: any) => item.title"
          show-search
          :filter-option="filterUser"
        />
      </div>
    </a-modal>

    <!-- 角色用户列表弹窗 -->
    <a-modal
      v-model:open="userListModalVisible"
      :title="roleUserListTitle"
      :width="560"
      :footer="null"
    >
      <a-spin :spinning="roleUsersLoading">
        <a-table
          v-if="roleUsersList.length > 0"
          :data-source="roleUsersList"
          :columns="roleUsersColumns"
          :pagination="false"
          size="small"
          row-key="id"
        />
        <a-empty v-else :description="$t('role.noUsersInRole')" />
      </a-spin>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, h } from 'vue'
import { message, Modal } from 'ant-design-vue'
import type { TableColumnsType, FormInstance } from 'ant-design-vue'
import {
  UserAddOutlined,
  TableOutlined,
  FileTextOutlined,
  DownOutlined
} from '@ant-design/icons-vue'
import { roleApi, type Role, type Permission, type RoleTemplate, type RoleUserItem, PERMISSION_MODULES, CONTRACT_PERMISSION_MODULES } from '@/modules/auth/api/role'
import { userApi, type UserInfo } from '@/modules/user/api'
import { formatDateTime } from '@/shared/utils'
import { useI18n } from 'vue-i18n'
import { runApiBatch, type BatchContext } from '@/shared/api/use-api-batch'

const { t } = useI18n()

const MODULE_ORDER = Object.keys(PERMISSION_MODULES)

function sortPermissionsForMatrix(list: Permission[]): Permission[] {
  return [...list].sort((a, b) => {
    const ia = MODULE_ORDER.indexOf(a.module)
    const ib = MODULE_ORDER.indexOf(b.module)
    const ra = ia === -1 ? 999 : ia
    const rb = ib === -1 ? 999 : ib
    if (ra !== rb) return ra - rb
    return a.name.localeCompare(b.name, 'zh-CN')
  })
}

// 响应式数据
const loading = ref(false)
const statsLoading = ref(false)
const submitting = ref(false)
const templatesLoading = ref(false)
const assigning = ref(false)

const roles = ref<Role[]>([])
const permissions = ref<Permission[]>([])
const roleTemplates = ref<RoleTemplate[]>([])
const allUsers = ref<any[]>([])

const stats = ref({
  total_roles: 0,
  system_roles: 0,
  custom_roles: 0,
  total_permissions: 0
})

// 弹窗状态
const roleModalVisible = ref(false)
const roleDetailVisible = ref(false)
const matrixModalVisible = ref(false)
const templateModalVisible = ref(false)
const assignModalVisible = ref(false)
const userListModalVisible = ref(false)
const roleUsersList = ref<RoleUserItem[]>([])
const roleUsersLoading = ref(false)
const roleForUserList = ref<Role | null>(null)

// 编辑状态
const isEditing = ref(false)
const selectedRole = ref<Role | null>(null)
const selectedModule = ref<string>()

// 表单引用
const roleFormRef = ref<FormInstance>()

// 搜索表单
const searchForm = reactive({
  search: '',
  status: undefined,
  is_system: undefined
})

// 角色表单
const roleForm = reactive({
  name: '',
  code: '',
  description: '',
  parent_id: undefined as number | undefined,
  sort_order: 0,
  permission_ids: [] as number[]
})

// 用户分配表单
const assignForm = reactive({
  user_ids: [] as string[]
})

// 分页
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number) => t('common.totalRecords', { total }),
  // 使用 onChange 处理页码变化
  onChange: (page: number, pageSize: number) => {
    pagination.current = page
    pagination.pageSize = pageSize
    loadRoles()
  },
  // 使用 onShowSizeChange 处理每页数量变化
  onShowSizeChange: (current: number, size: number) => {
    pagination.current = current
    pagination.pageSize = size
    loadRoles()
  }
})

// 选中行
const selectedRowKeys = ref<number[]>([])
const checkAll = ref(false)

// 获取可选择的角色（非系统角色）
const selectableRoles = computed(() => {
  return roles.value.filter(role => !role.is_system)
})

// 全选/取消全选
const onCheckAllChange = (checked: boolean) => {
  console.log('onCheckAllChange called:', checked)
  checkAll.value = checked
  const selectableKeys = selectableRoles.value.map(role => role.id)
  selectedRowKeys.value = checked ? [...selectableKeys] : []
}

// 单个复选框变化
const onCheckboxChange = (record: Role, checked: boolean) => {
  console.log('onCheckboxChange called:', record.name, 'checked:', checked, 'is_system:', record.is_system)
  
  if (record.is_system) {
    console.log('System role selection blocked:', record.name)
    return // 阻止系统角色被选择
  }
  
  if (checked) {
    selectedRowKeys.value = [...selectedRowKeys.value, record.id]
  } else {
    selectedRowKeys.value = selectedRowKeys.value.filter(key => key !== record.id)
  }
  
  // 更新全选状态
  const selectableKeys = selectableRoles.value.map(role => role.id)
  checkAll.value = selectedRowKeys.value.length === selectableKeys.length
}

// 表格列定义
const columns: TableColumnsType = computed(() => [
  {
    title: t('common.select'),
    key: 'selection',
    width: 50,
    align: 'center'
  },
  {
    title: t('role.roleName'),
    key: 'name',
    width: 200
  },
  {
    title: t('role.roleCode'),
    dataIndex: 'code',
    key: 'code',
    width: 150
  },
  {
    title: t('user.status'),
    key: 'status',
    width: 100,
    align: 'center'
  },
  {
    title: t('role.permissions'),
    key: 'permissions',
    width: 300
  },
  {
    title: t('role.userCount'),
    key: 'user_count',
    width: 100,
    align: 'center'
  },
  {
    title: t('role.sortOrder'),
    dataIndex: 'sort_order',
    key: 'sort_order',
    width: 80,
    align: 'center'
  },
  {
    title: t('user.createTimeLabel'),
    dataIndex: 'created_at',
    key: 'created_at',
    width: 150,
    customRender: ({ text }) => formatDateTime(text)
  },
  {
    title: t('common.actions'),
    key: 'action',
    width: 150,
    fixed: 'right'
  }
])

// 表单验证规则
const roleFormRules = computed(() => ({
  name: [
    { required: true, message: t('role.roleNameRequired') },
    { min: 2, max: 100, message: t('role.roleNameLength') }
  ],
  code: [
    { required: true, message: t('role.roleCodeRequired') },
    { pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/, message: t('role.roleCodePattern') }
  ]
}))

// 计算属性
const roleTreeData = computed(() => {
  const buildTree = (roles: Role[], parentId?: number): any[] => {
    return roles
      .filter(role => role.parent_id === parentId)
      .map(role => ({
        title: role.name,
        value: role.id,
        key: role.id,
        children: buildTree(roles, role.id)
      }))
  }
  return buildTree(roles.value)
})

const permissionTreeData = computed(() => {
  const groupedPermissions = permissions.value.reduce((acc, perm) => {
    if (!acc[perm.module]) {
      acc[perm.module] = []
    }
    acc[perm.module].push(perm)
    return acc
  }, {} as Record<string, Permission[]>)

  const moduleKeys = [
    ...MODULE_ORDER.filter((module) => groupedPermissions[module]),
    ...Object.keys(groupedPermissions).filter((module) => !MODULE_ORDER.includes(module))
  ]

  return moduleKeys.map((module) => {
    const perms = sortPermissionsForMatrix(groupedPermissions[module])
    const baseTitle = PERMISSION_MODULES[module as keyof typeof PERMISSION_MODULES] || module
    const isContract = (CONTRACT_PERMISSION_MODULES as readonly string[]).includes(module)
    return {
      title: isContract ? `${baseTitle}（客户端契约）` : baseTitle,
      key: module,
      children: perms.map(perm => ({
        title: perm.name,
        key: perm.id,
        isLeaf: true
      }))
    }
  })
})

const matrixColumns = computed(() => {
  const baseColumns = [
    {
      title: '角色',
      key: 'role',
      dataIndex: 'roleName',
      width: 150,
      fixed: 'left'
    }
  ]
  
  const permissionColumns = sortPermissionsForMatrix(permissions.value).map(perm => ({
    title: perm.name,
    key: `perm_${perm.id}`,
    width: 100,
    align: 'center' as const,
    permissionId: perm.id
  }))
  
  return [...baseColumns, ...permissionColumns]
})

const matrixData = computed(() => {
  return roles.value.map(role => {
    const rolePermissions = new Set((role.permissions || []).map(p => p.id))
    const data: any = {
      roleId: role.id,
      roleName: role.name
    }
    
    permissions.value.forEach(perm => {
      data[`perm_${perm.id}`] = rolePermissions.has(perm.id)
    })
    
    return data
  })
})

// 工具函数
const getStatusColor = (status: string) => {
  const colors = {
    active: 'green',
    inactive: 'default'
  }
  return colors[status as keyof typeof colors] || 'default'
}

const getStatusText = (status: string) => {
  const statusMap = {
    active: t('user.active'),
    inactive: t('user.inactive')
  }
  return statusMap[status as keyof typeof statusMap] || status
}

// 数据加载
const loadRoles = async (batchCtx?: BatchContext) => {
  const manageLoading = !batchCtx
  if (manageLoading) {
    loading.value = true
  }
  try {
    const params = {
      skip: (pagination.current - 1) * pagination.pageSize,
      limit: pagination.pageSize,
      ...searchForm
    }

    const response = await roleApi.getRoles(params, batchCtx?.requestConfig)
    roles.value = response.items
    pagination.total = response.total

    stats.value.total_roles = response.total
    stats.value.system_roles = response.items.filter(r => r.is_system).length
    stats.value.custom_roles = response.items.filter(r => !r.is_system).length
  } catch (error) {
    if (!batchCtx) {
      console.error('Load roles error:', error)
    }
    throw error
  } finally {
    if (manageLoading) {
      loading.value = false
    }
  }
}

const loadPermissions = async (batchCtx?: BatchContext) => {
  try {
    const response = await roleApi.getPermissions(undefined, batchCtx?.requestConfig)
    permissions.value = sortPermissionsForMatrix(response)
    stats.value.total_permissions = response.length
  } catch (error) {
    if (!batchCtx) {
      console.error('Load permissions error:', error)
    }
    throw error
  }
}

const loadRoleTemplates = async () => {
  templatesLoading.value = true
  try {
    const response = await roleApi.getRoleTemplates()
    roleTemplates.value = response
  } catch (error) {
    console.error('Load role templates error:', error)
  } finally {
    templatesLoading.value = false
  }
}

const loadAllUsers = async () => {
  try {
    const response = await userApi.getUsers({ limit: 100 })
    allUsers.value = (response.items || []).map((user: UserInfo) => ({
      key: user.id.toString(),
      title: `${user.name} (${user.username})`,
      description: user.email
    }))
  } catch (error) {
    console.error('Load users error:', error)
  }
}

// 事件处理
const handleSearch = async () => {
  pagination.current = 1
  await loadRoles()
}

const resetSearch = async () => {
  searchForm.search = ''
  searchForm.status = undefined
  searchForm.is_system = undefined
  await handleSearch()
}


// 处理表格变化（排序和筛选）
// 注意：分页变化已经通过 pagination 配置的 onChange/onShowSizeChange 处理
// @change 事件会传递三个参数：(pagination, filters, sorter)
const handleTableChange = (pag: any, filters: any, sorter: any) => {
  // 分页变化已经在 pagination 的 onChange/onShowSizeChange 中处理
  // 这里只处理排序和筛选（如果需要的话）
  // 当前这个表格没有排序和筛选功能，所以这里可以留空或添加相应逻辑
}

// 角色操作
const showCreateModal = () => {
  isEditing.value = false
  resetRoleForm()
  roleModalVisible.value = true
}

const viewRole = async (role: Role) => {
  try {
    const response = await roleApi.getRole(role.id)
    selectedRole.value = response
    roleDetailVisible.value = true
  } catch (error) {
    console.error('Get role detail error:', error)
  }
}

const editRole = async (role: Role) => {
  try {
    const response = await roleApi.getRole(role.id)
    selectedRole.value = response
    isEditing.value = true
    
    // 填充表单
    roleForm.name = response.name
    roleForm.code = response.code
    roleForm.description = response.description || ''
    roleForm.parent_id = response.parent_id
    roleForm.sort_order = response.sort_order
    roleForm.permission_ids = (response.permissions || []).map(p => p.id)
    
    roleModalVisible.value = true
  } catch (error) {
    console.error('Get role for edit error:', error)
  }
}

const resetRoleForm = () => {
  roleForm.name = ''
  roleForm.code = ''
  roleForm.description = ''
  roleForm.parent_id = undefined
  roleForm.sort_order = 0
  roleForm.permission_ids = []
}

const handleRoleSubmit = async () => {
  try {
    await roleFormRef.value?.validate()
    submitting.value = true
    
    // 确保 permission_ids 是数字数组
    const permissionIds = roleForm.permission_ids.map(id => 
      typeof id === 'string' ? parseInt(id, 10) : id
    ).filter(id => !isNaN(id))
    
    if (isEditing.value && selectedRole.value) {
      // 更新角色
      const updateData = {
        name: roleForm.name,
        code: roleForm.code,
        description: roleForm.description,
        parent_id: roleForm.parent_id,
        sort_order: roleForm.sort_order,
        permission_ids: permissionIds
      }
      
      await roleApi.updateRole(selectedRole.value.id, updateData)
      message.success(t('role.roleUpdateSuccess'))
    } else {
      // 创建角色
      const createData = {
        name: roleForm.name,
        code: roleForm.code,
        description: roleForm.description,
        parent_id: roleForm.parent_id,
        sort_order: roleForm.sort_order,
        permission_ids: permissionIds
      }
      
      await roleApi.createRole(createData)
      message.success(t('role.roleCreateSuccess'))
    }
    
    roleModalVisible.value = false
    await loadRoles()
  } catch (error) {
    console.error('Submit role error:', error)
  } finally {
    submitting.value = false
  }
}

const handleRoleCancel = () => {
  roleModalVisible.value = false
  resetRoleForm()
}

const deleteRole = (role: Role) => {
  Modal.confirm({
    title: t('role.confirmDelete'),
    content: t('role.deleteRoleConfirm', { name: role.name }),
    okText: t('common.ok'),
    cancelText: t('common.cancel'),
    okType: 'danger',
    onOk: async () => {
      try {
        await roleApi.deleteRole(role.id)
        message.success(t('role.roleDeleteSuccess'))
        await loadRoles()
      } catch (error) {
        console.error('Delete role error:', error)
      }
    }
  })
}

const assignUsers = async (role: Role) => {
  selectedRole.value = role
  await loadAllUsers()
  
  // 获取已分配的用户
  const roleUsers = role.user_count ? [] : [] // 这里需要从API获取具体的用户列表
  assignForm.user_ids = roleUsers
  
  assignModalVisible.value = true
}

// 权限操作
const selectAllPermissions = () => {
  roleForm.permission_ids = permissions.value.map(p => p.id)
}

const clearAllPermissions = () => {
  roleForm.permission_ids = []
}

const filterPermissionsByModule = () => {
  if (!selectedModule.value) {
    return
  }
  
  const modulePermissions = permissions.value
    .filter(p => p.module === selectedModule.value)
    .map(p => p.id)
  
  roleForm.permission_ids = [...new Set([...roleForm.permission_ids, ...modulePermissions])]
}

// 权限矩阵
const showPermissionMatrix = () => {
  matrixModalVisible.value = true
}

const handleMatrixChange = async (roleId: number, permissionId: number, checked: boolean) => {
  try {
    const role = roles.value.find(r => r.id === roleId)
    if (!role) return
    
    const currentPermissionIds = (role.permissions || []).map(p => p.id)
    let newPermissionIds: number[]
    
    if (checked) {
      newPermissionIds = [...currentPermissionIds, permissionId]
    } else {
      newPermissionIds = currentPermissionIds.filter(id => id !== permissionId)
    }
    
    await roleApi.updateRole(roleId, { permission_ids: newPermissionIds })
    
    // 更新本地数据
    const permission = permissions.value.find(p => p.id === permissionId)
    if (permission) {
      if (!role.permissions) {
        role.permissions = []
      }
      if (checked) {
        role.permissions.push(permission)
      } else {
        role.permissions = role.permissions.filter(p => p.id !== permissionId)
      }
    }
    
    message.success(t('role.permissionUpdateSuccess'))
  } catch (error) {
    console.error('Update permission error:', error)
  }
}

// 角色模板
const showTemplateModal = async () => {
  await loadRoleTemplates()
  templateModalVisible.value = true
}

const applyTemplate = (template: RoleTemplate) => {
  Modal.confirm({
    title: t('role.applyTemplate'),
    content: t('role.applyTemplateConfirm', { name: template.name }),
    okText: t('common.ok'),
    cancelText: t('common.cancel'),
    onOk: async () => {
      if (!selectedRole.value) {
        message.warning(t('role.pleaseSelectRole'))
        return
      }
      
      try {
        await roleApi.applyTemplateToRole(template.id, selectedRole.value.id)
        message.success(t('role.templateApplySuccess'))
        await loadRoles()
        templateModalVisible.value = false
      } catch (error) {
        console.error('Apply template error:', error)
      }
    }
  })
}

const viewTemplate = (template: RoleTemplate) => {
  Modal.info({
    title: t('role.templateDetails', { name: template.name }),
    content: h('div', [
      h('p', [
        h('strong', '描述:'),
        template.description
      ]),
      h('p', [
        h('strong', '类型:'),
        template.is_builtin ? '内置模板' : '自定义模板'
      ]),
      h('p', [
        h('strong', '权限配置:')
      ]),
      h('pre', JSON.stringify(template.permissions, null, 2))
    ]),
    width: 600
  })
}

// 用户分配
const handleUserAssign = async () => {
  if (!selectedRole.value) return
  
  try {
    assigning.value = true
    const userIds = assignForm.user_ids.map(id => parseInt(id))
    await roleApi.assignUsersToRole(selectedRole.value.id, userIds)
    message.success(t('role.userAssignSuccess'))
    assignModalVisible.value = false
    await loadRoles()
  } catch (error) {
    console.error('Assign users error:', error)
  } finally {
    assigning.value = false
  }
}

const filterUser = (inputValue: string, option: any) => {
  return option.title.toLowerCase().includes(inputValue.toLowerCase())
}

const roleUserListTitle = computed(() => {
  const name = roleForUserList.value?.name
  return name ? `${t('role.userList')} - ${name}` : t('role.userList')
})

const roleUsersColumns: TableColumnsType = [
  { title: t('user.username'), dataIndex: 'username', key: 'username', width: 120 },
  { title: t('user.name'), dataIndex: 'name', key: 'name', width: 120 },
  { title: t('user.email'), dataIndex: 'email', key: 'email', ellipsis: true }
]

const viewRoleUsers = async (role: Role) => {
  roleForUserList.value = role
  userListModalVisible.value = true
  roleUsersList.value = []
  roleUsersLoading.value = true
  try {
    const list = await roleApi.getRoleUsers(role.id)
    roleUsersList.value = list
  } catch (error) {
    console.error('Load role users error:', error)
  } finally {
    roleUsersLoading.value = false
  }
}

// 批量操作
const batchDelete = () => {
  const systemRoles = selectedRowKeys.value.filter(id => {
    const role = roles.value.find(r => r.id === id)
    return role?.is_system
  })
  
  if (systemRoles.length > 0) {
    message.warning(t('role.cannotDeleteSystemRoles'))
    return
  }
  
  Modal.confirm({
    title: t('role.confirmBatchDelete'),
    content: t('role.batchDeleteConfirm', { count: selectedRowKeys.value.length }),
    okText: t('common.ok'),
    cancelText: t('common.cancel'),
    okType: 'danger',
    onOk: async () => {
      try {
        const results = await runApiBatch(
          selectedRowKeys.value.map((id) => (ctx) => roleApi.deleteRole(id, ctx.requestConfig)),
        )
        if (results.some((r) => r.status === 'fulfilled')) {
          message.success(t('role.batchDeleteSuccess'))
          selectedRowKeys.value = []
          await loadRoles()
        }
      } catch (error) {
        console.error('Batch delete error:', error)
      }
    }
  })
}

// 初始化
onMounted(() => {
  runApiBatch([
    (ctx) => loadRoles(ctx),
    (ctx) => loadPermissions(ctx),
  ])
})
</script>

<style lang="less" scoped>
.role-management {
  padding: 24px;
  
  .page-header {
    margin-bottom: 24px;
    
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 24px; // 添加间距
      
      h2 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        flex-shrink: 0; // 防止标题被压缩
      }
      
      .header-actions {
        flex-shrink: 0; // 防止按钮被压缩
      }
    }
  }
  
  .stats-cards {
    margin-bottom: 24px;
  }
  
  .search-card {
    margin-bottom: 24px;
  }
  
  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .role-name {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .name {
      font-weight: 500;
    }
  }
  
  .permission-alert {
    margin-bottom: 12px;
  }

  .permission-config {
    .permission-header {
      margin-bottom: 12px;
      padding-bottom: 12px;
      border-bottom: 1px solid #f0f0f0;
    }
    
    .permission-tree {
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      padding: 8px;
    }
  }
  
  .role-detail {
    .permission-list {
      margin-top: 16px;
      
      .permission-item {
        .permission-name {
          font-weight: 500;
          margin-bottom: 4px;
        }
        
        .permission-code {
          font-size: 12px;
          color: #666;
          margin-bottom: 8px;
        }
        
        .permission-module {
          text-align: right;
        }
      }
    }
  }
  
  .permission-matrix {
    :deep(.ant-table-thead > tr > th) {
      text-align: center;
      font-size: 12px;
      padding: 8px 4px;
    }
    
    :deep(.ant-table-tbody > tr > td) {
      text-align: center;
      padding: 8px 4px;
    }
  }
  
  .role-templates {
    .template-title {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  
  .user-assign {
    :deep(.ant-transfer) {
      .ant-transfer-list {
        width: 250px;
        height: 400px;
      }
    }
  }
  
  .text-red {
    color: #ff4d4f;
  }
  
  // 隐藏表格展开图标
  :deep(.ant-table-row-expand-icon) {
    display: none !important;
  }
  
  :deep(.ant-table-row-expand-icon-collapsed) {
    display: none !important;
  }
  
  :deep(.ant-table-row-expand-icon-expanded) {
    display: none !important;
  }
}
</style>